/**
 * Builds the Firestore payload for a saved non-EPA summary, naming every
 * channel-specific field after the tab it came from so a fax entry carries no
 * call wording and no empty call fields (and vice versa).
 */

import { CallingHistoryLog } from "../../../../pages/callingWorklist/services/callingHistoryService";
import { CallingFormData } from "../../../sidePanel/submissionSummarySidePanel/utils/buildCallingFormDataFromOrder";
import { CallSummaryMode } from "./callSummaryMode";

/** Document fields written for a fax attachment, when one was uploaded. */
export interface HistoryLogAttachment {
  attachment_path: string;
  attachment_name: string;
  attachment_url: string;
}

/** Everything the pane knows when the user hits Save. */
export interface HistoryLogInput {
  mode: CallSummaryMode;
  formData: CallingFormData;
  /** Full display timestamp captured when the modal was opened. */
  dateTime: string;
  /** Date part of {@link dateTime}, in MM/DD/YYYY. */
  loggedDate: string;
  /** Reason the order was routed to non-EPA outreach. */
  reason: string;
  secondStpStatus: string;
  createdBy: string;
  attachment: HistoryLogAttachment | null;
}

/** Payload accepted by `saveCallingHistoryLog` (id and created_at are added there). */
type HistoryLogPayload = Omit<CallingHistoryLog, "id" | "created_at">;

/**
 * Fields shared by both channels.
 *
 * @input input - Values captured when the summary was saved
 * @return Channel-agnostic payload fields
 */
const buildSharedFields = (input: HistoryLogInput): HistoryLogPayload => ({
  mode: input.mode,
  case_id: input.formData.caseId,
  comment: input.formData.comment,
  response_status: input.formData.responseStatus,
  case_status: input.formData.caseStatus,
  second_stp_status: input.secondStpStatus,
  next_action: input.formData.nextAction,
  follow_up_date: input.formData.followUpDate,
  created_by: input.createdBy,
});

/**
 * Builds the payload for one saved summary, using fax-named fields on the Fax
 * tab and call-named fields on the Call tab.
 *
 * @input input - Values captured when the summary was saved
 * @return Payload to persist under the channel's history collection
 */
export const buildHistoryLogPayload = (
  input: HistoryLogInput,
): HistoryLogPayload => {
  const shared = buildSharedFields(input);

  if (input.mode === CallSummaryMode.FAX) {
    return {
      ...shared,
      faxed_date: input.loggedDate,
      faxed_at: input.dateTime,
      poc: input.formData.callingPoc,
      non_epa_reason: input.reason,
      fax_required: input.formData.callRequired,
      fax_number: input.formData.faxNumber,
      sent_to: input.formData.sentTo,
      ...(input.attachment ?? {}),
    };
  }

  return {
    ...shared,
    called_date: input.loggedDate,
    called_at: input.dateTime,
    calling_poc: input.formData.callingPoc,
    calling_reason: input.reason,
    call_required: input.formData.callRequired,
    rep_name: input.formData.repName,
    phone_number: input.formData.phoneNumber,
  };
};
